import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const TimerContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px;
`;

const TimerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const RecordingIndicator = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #ff3b30;
    animation: pulse 2s infinite;

    @keyframes pulse {
        0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.7);
        }
        70% {
            transform: scale(1);
            box-shadow: 0 0 0 6px rgba(255, 59, 48, 0);
        }
        100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(255, 59, 48, 0);
        }
    }
`;

const TimeText = styled.span`
    font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 1.1rem;
    font-weight: 500;
    letter-spacing: 0.5px;
    color: ${props => props.$timerColor || 'inherit'};
    transition: color 0.5s ease;
`;

const PacingLabel = styled.span`
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.5);
    letter-spacing: 0.3px;
`;

const DurationDisplay = ({ recording, pacingConfig }) => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [recordingStartDateTime, setRecordingStartDateTime] = useState(null);

    useEffect(() => {
        if (recording) {
            const startDateTime = new Date();
            setRecordingStartDateTime(startDateTime);
            setCurrentTime(new Date());
            const intervalId = setInterval(() => {
                setCurrentTime(new Date());
            }, 1000);

            return () => clearInterval(intervalId);
        } else {
            setRecordingStartDateTime(null);
            setCurrentTime(null);
        }
    }, [recording]);

    if (!recording) {
        return null;
    }

    const diffInMs = Math.abs(currentTime - recordingStartDateTime);
    const totalSeconds = Math.floor(diffInMs / 1000);

    // Convert to hours, minutes, and seconds
    const hours = Math.floor(diffInMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000);

    // Format as mm:ss or hh:mm:ss if hours > 0
    const formattedTime = hours > 0
        ? `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        : `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    // Determine timer color based on pacing config
    let timerColor = null;
    if (pacingConfig) {
        if (totalSeconds <= pacingConfig.greenUntil) {
            timerColor = '#34C759'; // green
        } else if (totalSeconds <= pacingConfig.yellowUntil) {
            timerColor = '#FFD60A'; // yellow
        } else {
            timerColor = '#FF3B30'; // red
        }
    }

    return (
        <TimerContainer>
            <TimerRow>
                <RecordingIndicator />
                <TimeText $timerColor={timerColor}>{formattedTime}</TimeText>
            </TimerRow>
            {pacingConfig && pacingConfig.targetLabel && (
                <PacingLabel>{pacingConfig.targetLabel}</PacingLabel>
            )}
        </TimerContainer>
    );
};

export default DurationDisplay;
